import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_URL } from '../constants/constant';
import { projectConfig } from '../config/projectConfig';
import { getToken, removeToken } from '../helpers/tokenStorage';
import { purgeStoredState } from 'redux-persist';
import {
  persistAuthConfig,
  persistUserConfig,
  persistHandoverConfig,
  persistHandoverTaskConfig,
  persistHandoverCommentConfig,
} from '../store/persistConfigs';
import { firebaseAuth } from '../config/firebaseClient';
import { signOut as signOutFirebaseUser } from 'firebase/auth';

const AUTH_ERROR_MARKERS = [
  'auth/argument-error',
  'auth/id-token-expired',
  'auth/id-token-revoked',
  'auth/invalid-id-token',
  'auth/session-cookie-expired',
  'auth/session-cookie-revoked',
  'auth/user-disabled',
  'auth/user-token-expired',
];

const isUnauthorizedError = (result: any) => {
  const status = result?.error?.status;
  if (status === 401) {
    return true;
  }

  if (status !== 500) {
    return false;
  }

  const message =
    typeof result?.error?.data?.message === 'string' ? result.error.data.message : '';

  return AUTH_ERROR_MARKERS.some(marker => message.includes(marker));
};

// Base query factory for creating service-specific queries
const createServiceBaseQuery = (serviceUrl: string) =>
  fetchBaseQuery({
    baseUrl: serviceUrl,
    credentials: 'omit',
    prepareHeaders: headers => {
      const token = getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

// Service-specific base queries
const authBaseQuery = createServiceBaseQuery(projectConfig.apiEndpoints.auth);
const userBaseQuery = createServiceBaseQuery(projectConfig.apiEndpoints.user);
const handoverBaseQuery = createServiceBaseQuery(projectConfig.apiEndpoints.handover);
const aiBaseQuery = createServiceBaseQuery(projectConfig.apiEndpoints.ai);

// Legacy base query (fallback)
const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL.TEST,
  credentials: 'include',
  prepareHeaders: headers => {
    const token = getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Custom base query wrapper with 401 handling
const createCustomBaseQuery = (serviceBaseQuery: ReturnType<typeof fetchBaseQuery>) =>
  async (args: any, api: any, extraOptions: any) => {
    const result = await serviceBaseQuery(args, api, extraOptions);

    if (isUnauthorizedError(result)) {
      // Token expired or invalid
      try {
        await signOutFirebaseUser(firebaseAuth);
      } catch (error) {
        console.error('Firebase client logout failed:', error);
      }
      removeToken();
      await purgeStoredState(persistAuthConfig);
      await purgeStoredState(persistUserConfig);
      await purgeStoredState(persistHandoverConfig);
      await purgeStoredState(persistHandoverTaskConfig);
      await purgeStoredState(persistHandoverCommentConfig);
      window.location.href = '/';
    }

    return result;
  };

// Export service-specific custom base queries
export const customAuthBaseQuery = createCustomBaseQuery(authBaseQuery);
export const customUserBaseQuery = createCustomBaseQuery(userBaseQuery);
export const customHandoverBaseQuery = createCustomBaseQuery(handoverBaseQuery);
export const customAiBaseQuery = createCustomBaseQuery(aiBaseQuery);

// Legacy export (for backward compatibility)
export const customBaseQuery = createCustomBaseQuery(baseQuery);
